import { DataService } from '../DataService';
import { ServiceType } from '../../types/services';
import { ContactsApi } from './contacts';
import { LabelsApi } from './labels';
import { BookingsApi } from './bookings';
import { TasksApi } from './tasks';
import { AttachmentsApi } from './attachments';
import { AnalyticsApi } from './analytics';
import { ContactHistoryApi } from './contactHistory';
import { ServiceTypesApi } from './serviceTypes';
import { StaffApi } from './staff';
import { ResourcesApi } from './resources';
import { InboxService } from './inboxService';
import { ContactAttachment } from '../../types/contacts';
import { AuthService } from '../AuthService';
import { rsAuth } from '../rsAuthClient';
import { callAdminApi } from './wixAdminApi';


export class ApiDataService implements DataService {
  // Implement the required auth property (replace with real implementation as needed)
  auth: AuthService = {
    login: async (idToken: string) => rsAuth.login(idToken),
    registerBackend: (email, password) => rsAuth.registerBackend(email, password),
    resetPassword: (email) => rsAuth.resetPassword(email),
    // handleOAuthCallback: not implemented in rsAuth
    handleOAuthCallback: async () => { throw new Error('Not implemented'); },
    verifyLogin: async () => {
      const state = await rsAuth.checkLoginStatus();
      return {
        isLoggedIn: state.isLoggedIn,
        user: state.user === null ? undefined : state.user,
      };
    },
    logout: () => Promise.resolve(rsAuth.logout()),
    // isLoggedIn: not implemented in rsAuth
    isLoggedIn: async () => false,
    updateUserPreferences: (preferences) => rsAuth.updateUserPreferences(preferences),
  };
  getBookingsByContactIdAndDateRange: (contactId: string, startDate: Date, endDate: Date) => Promise<import('../../types/bookings').Booking[]>;
  // Service APIs
  labels = new LabelsApi();
  bookings = new BookingsApi();
  tasks = new TasksApi();
  attachments = new AttachmentsApi();
  analytics = new AnalyticsApi();
  contactHistory = new ContactHistoryApi();
  staff = new StaffApi();
  resources = new ResourcesApi();

  // Delegate inbox methods
  getConversations = InboxService.getConversations.bind(InboxService);
  getConversation = InboxService.getConversation.bind(InboxService);
  getMessages = InboxService.getMessages.bind(InboxService);
  sendMessage = InboxService.sendMessage.bind(InboxService);
  startConversation = InboxService.startConversation.bind(InboxService);
  markAsRead = InboxService.markAsRead.bind(InboxService);
  archiveConversation = InboxService.archiveConversation.bind(InboxService);


  // Services that require initialization with dependencies
  contacts: ContactsApi;
  serviceTypes: ServiceTypesApi;

  constructor(
    serviceTypeProvider?: () => Promise<Array<{ type: ServiceType; imageUrl: string; serviceId: string }>>
  ) {
    // Initialize the ContactsApi
    this.contacts = new ContactsApi();

    // Initialize the ServiceTypesApi with the provider
    this.serviceTypes = new ServiceTypesApi(serviceTypeProvider);
    // Delegate after bookings is initialized
    this.getBookingsByContactIdAndDateRange = this.bookings.getBookingsByContactIdAndDateRange.bind(this.bookings);
  }

  // Use the attachments service for attachment operations

  /**
   * Debug: Log the raw contact data returned from ContactsApi.getContactById before returning to the frontend
   */
  async getContactById(id: string, preserveOrder?: import('../../types/contacts').CustomField[]): Promise<import('../../types/contacts').Contact | null> {
    const result = await this.contacts.getContactById(id, preserveOrder);
    return result;
  }
  async getAttachments(contactId: string): Promise<ContactAttachment[]> {
    return this.attachments.getAttachments(contactId);
  }

  async addAttachment(contactId: string, file: File): Promise<ContactAttachment> {
    return this.attachments.addAttachments(contactId, file);
  }

  async deleteAttachment(contactId: string, attachmentId: string): Promise<boolean> {
    return this.attachments.deleteAttachment(contactId, attachmentId);
  }

  async downloadAttachment(contactId: string, attachmentId: string): Promise<string> {
    return this.attachments.downloadAttachment(contactId, attachmentId);
  }

  getServiceTypes = async (): Promise<Array<{ type: ServiceType; imageUrl: string; serviceId: string }>> => {
    return this.serviceTypes.getServiceTypes();
  }

  async getGlobalFieldOrder(): Promise<string[]> {
    try {
      // Use the modern callAdminApi helper for consistency and cleaner code.
      const result = await callAdminApi('get', 'fieldOrder');
      // The backend returns { fieldOrder: [...] }, so we access that property.
      return (result as { fieldOrder?: string[] }).fieldOrder || [];
    } catch (error) {
      console.error('Error getting global field order:', error);
      // Return an empty array on error to prevent the UI from crashing.
      return [];
    }
  }

  async saveGlobalFieldOrder(fieldOrder: string[]): Promise<boolean> {
    try {
      // Use callAdminApi and pass the fieldOrder array in the params object.
      await callAdminApi('set', 'fieldOrder', { fieldOrder });
      return true;
    } catch (error) {
      console.error('Error saving global field order:', error);
      // The callAdminApi function throws on failure, which is caught here.
      return false;
    }
  }

  // Delegate attachment operations to AttachmentsApi
  async listAttachmentsWithPreview(contactId: string, paging?: { limit?: number; offset?: number }): Promise<{ attachments: Array<{ id: string; url: string; name: string; type: string }> }> {
    const result = await this.attachments.listAttachmentsWithPreview(contactId, paging);
    return {
      attachments: result.attachments.map(attachment => ({
        id: attachment.id as string,
        url: attachment.url as string,
        name: attachment.name as string,
        type: attachment.type as string
      }))
    };
  }
  async getAttachmentWithFallback(contactId: string, attachmentId: string): Promise<{ id: string; url: string; name: string; type: string }> {
    const result = await this.attachments.getAttachmentWithFallback(contactId, attachmentId);
    return {
      id: result.id as string,
      url: result.url as string,
      name: result.name as string,
      type: result.type as string
    };
  } async uploadAttachmentWithFullValidation(contactId: string, fileName: string, fileType: string, fileData: string): Promise<{ id: string; url: string }> {
    const result = await this.attachments.uploadAttachmentWithFullValidation(contactId, fileName, fileType, fileData);
    return {
      id: result.attachmentId,
      url: result.downloadUrl
    };
  }

  async generateDownloadUrlWithFallback(contactId: string, attachmentId: string): Promise<{ downloadUrl: string; originalUrl?: string; fallback?: boolean }> {
    return this.attachments.generateDownloadUrlWithFallback(contactId, attachmentId);
  }
  async convertWixImageUrlWithFallback(wixImageUrl: string): Promise<{ downloadUrl: string; originalUrl?: string; fallback?: boolean }> {
    return this.attachments.convertWixImageUrlWithFallback(wixImageUrl);
  }

  // Delegate service operations to ServiceTypesApi
  async queryWixBookingsServices(params: Record<string, unknown> = {}): Promise<Array<{ type: ServiceType; imageUrl: string }>> {
    return this.serviceTypes.queryWixBookingsServices(params);
  }
  async getWixBookingsService(serviceId: string): Promise<{ type: ServiceType; imageUrl: string } | null> {
    return this.serviceTypes.getWixBookingsService(serviceId);
  }

  // Delegate booking methods to the BookingsApi using arrow function properties, matching contacts and labels
  getAllBookings = this.bookings.getAllBookings.bind(this.bookings);
  getBookingById = this.bookings.getBookingById.bind(this.bookings);
  createBooking = this.bookings.createBooking.bind(this.bookings);
  updateBooking = this.bookings.updateBooking.bind(this.bookings);
  deleteBooking = this.bookings.deleteBooking.bind(this.bookings);
  getBookingsByDateRange = this.bookings.getBookingsByDateRange.bind(this.bookings);
  getBookingsForDay = this.bookings.getBookingsForDay.bind(this.bookings);
  getUpcomingBookings = this.bookings.getUpcomingBookings.bind(this.bookings);
  updateBookingStatus = this.bookings.updateBookingStatus.bind(this.bookings);
  setBookingAttended = this.bookings.setBookingAttended.bind(this.bookings);
  getBookingsAttendance = this.bookings.getBookingsAttendance.bind(this.bookings);
  cancelBooking = this.bookings.cancelBooking.bind(this.bookings);
  getBookingsAndBlockedSlotsByDateRange = this.bookings.getBookingsAndBlockedSlotsByDateRange.bind(this.bookings);
  getBlockedSlotsByDateRange = this.bookings.getBlockedSlotsByDateRange.bind(this.bookings);
}
